import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Invitation,
  Inviter,
  Registerer,
  RegistererOptions,
  Session,
  SessionState,
  UserAgent,
  UserAgentOptions,
  InvitationAcceptOptions,
  Web,
} from 'sip.js'
import { v4 as uuidv4 } from 'uuid'
import { Howl } from 'howler'
import { OutgoingByeRequest } from 'sip.js/lib/core'
import {
  SessionDescriptionHandlerConfiguration,
  TransportOptions,
} from 'sip.js/lib/platform/web'
import { get } from '~/utils/api'
import { useHistory } from 'react-router-dom'
import { WebsocketContext } from './WebsocketConn'
import { setStorage } from '~/components/calls/show/Describe'
import { DeviceId, useDeviceChooserStore } from './DeviceChooser'
import { IframeContext } from './IframeConn'
import { ClientOnShow } from '~/components/clients/Show'
import { NotificationsContext } from './Notifications'
import {
  CallOnShow,
  CallOnShowRaw,
  TelemarketingContactOnShow,
} from '~/components/calls/types'
import { convertCall } from './call/callConverter'

export type RegistrationStatus = 'loading' | 'success' | 'fail'
export type RegistrationStatusWithDisabled = RegistrationStatus | 'disabled'
type UserAgentConnectionStatus = 'connected' | 'disconnected'

type GetCallDataResp = {
  call: CallOnShowRaw
  client: ClientOnShow
  ivr: string
}

export type Call = {
  uuid: string
  id?: number
  sessionId: string
  isIncoming: boolean
  number: string
  establishedAt?: Date
  terminatedAt?: Date
  sessionState: SessionState
  bye: () => Promise<OutgoingByeRequest>
  apiData?: CallOnShow
  client?: ClientOnShow
  localMediaStream?: MediaStream
  remoteMediaStream?: MediaStream
  peerConnection?: RTCPeerConnection
  muted: boolean
  sendDtmf: (signal: string) => void
}

export type IncomingCall = Call & {
  isIncoming: true
  answer: () => void
  reject: () => void
  transferedToMeStatus: false | 'started' | 'accepted'
  setTransferStatus: (status: TransferStatus) => void
  transferStatus: TransferStatus
}

type TransferStatus = false | 'started' | 'dtmfSent' | 'accepted'

export type OutgoingCall = Call & {
  isIncoming: false
  cancel: () => void
}

export type CallBoth = IncomingCall | OutgoingCall

type Calls = CallBoth[]

type MakeCall = (number: string, payload?: object) => void

type State = {
  registrationStatus: RegistrationStatusWithDisabled
  userAgentConnectionStatus: UserAgentConnectionStatus
  calls: Calls
  makeCall: MakeCall
  activeCall?: CallBoth
  activeEstablishedCall?: CallBoth
  incomingCall?: IncomingCall
  hide: () => void
  shownCall?: CallBoth
  toggleMute: (uuid: string) => void
}

export const SipContext = React.createContext<State>({
  registrationStatus: 'loading',
  userAgentConnectionStatus: 'disconnected',
  calls: [],
  makeCall: () => {},
  hide: () => {},
  toggleMute: () => {},
})

// const domain = 'sip.cloudivr.io'
// const server = `wss://sip.cloudivr.io`

const inboundSound = new Howl({
  src: ['/sounds/phone.mp3'],
  volume: 0.7,
  loop: true,
  html5: true,
})

const inboundSoundPause = () => {
  inboundSound.pause()

  setTimeout(() => inboundSound.pause(), 500)
  setTimeout(() => inboundSound.pause(), 1000)
  setTimeout(() => inboundSound.pause(), 2000)
}

const outboundSound = new Howl({
  src: ['/sounds/notification_sound3.mp3'],
  loop: true,
  html5: true,
})

const outboundSoundPause = () => {
  outboundSound.pause()

  setTimeout(() => outboundSound.pause(), 500)
  setTimeout(() => outboundSound.pause(), 1000)
  setTimeout(() => outboundSound.pause(), 2000)
}

const endSound = new Howl({
  src: ['/sounds/end.wav'],
  html5: true,
})

const sessionStateHandler = (
  calls: Calls,
  session: Session,
  state: SessionState,
  audioEl: HTMLAudioElement,
  outputId: DeviceId
) =>
  calls.map(call =>
    call.sessionId == session.id
      ? (() => {
          const newCall = { ...call, sessionState: state }

          switch (state) {
            case SessionState.Establishing:
              return { ...newCall }
            case SessionState.Established:
              const sdh = session.sessionDescriptionHandler

              if (!sdh || !(sdh instanceof Web.SessionDescriptionHandler)) {
                throw new Error('Invalid session description handler.')
              }

              if (audioEl) {
                assignStream(sdh.remoteMediaStream, audioEl, outputId)
              }

              return {
                ...newCall,
                establishedAt: new Date(),
                localMediaStream: sdh.localMediaStream,
                remoteMediaStream: sdh.remoteMediaStream,
                peerConnection: sdh.peerConnection,
              }
            case SessionState.Terminated:
              // howler
              endSound.play()

              return {
                ...newCall,
                terminatedAt: new Date(),
                localMediaStream: undefined,
                remoteMediaStream: undefined,
              }
            default:
              return newCall
          }
        })()
      : call
  )

// Assign a MediaStream to an HTMLMediaElement and update if tracks change.
function assignStream(
  stream: MediaStream,
  element: HTMLMediaElement,
  id: DeviceId
): void {
  // Set element source.
  // Safari does not allow calling .play() from a non user action
  element.autoplay = true
  element.srcObject = stream

  if (id) {
    element['setSinkId'](id)
  }

  // Load and start playback of media.
  element.play().catch((error: Error) => {
    console.error('Failed to play media')
    console.error(error)
  })

  // If a track is added, load and restart playback of media.
  stream.onaddtrack = (): void => {
    element.load() // Safari does not work otheriwse
    element.play().catch((error: Error) => {
      console.error('Failed to play remote media on add track')
      console.error(error)
    })
  }

  // If a track is removed, load and restart playback of media.
  stream.onremovetrack = (): void => {
    element.load() // Safari does not work otheriwse
    element.play().catch((error: Error) => {
      console.error('Failed to play remote media on remove track')
      console.error(error)
    })
  }
}

const sendDtmf = (session: Session) => (signal: string) => {
  console.log('DTMF', signal)
  session.info({
    requestOptions: {
      body: {
        contentDisposition: 'render',
        contentType: 'application/dtmf-relay',
        content: `Signal=${signal}\r\nDuration=1000`,
      },
    },
  })
}

type Props = {
  login: string
  password: string
  domain: string
  children: React.ReactNode
  iceServers: IceServer[]
}

const SipConn: React.FC<Props> = ({
  children,
  login,
  password,
  domain,
  iceServers,
}) => {
  const history = useHistory()
  const audioRef = useRef<HTMLAudioElement>(null)
  const makeCallRef = useRef<MakeCall>(() => {})
  const [calls, setCalls] = useState<Calls>([])
  const [shownCallId, setShownCallId] = useState<string | undefined>()
  const unregisterRef = useRef<() => void | undefined>()
  const registerRef = useRef<() => void | undefined>()
  const {
    userChannel,
    tabUuid,
    activeTab,
    status: wsStatus,
  } = useContext(WebsocketContext)

  const iframeContext = useContext(IframeContext)
  const notificationsContext = useContext(NotificationsContext)

  const { input, output } = useDeviceChooserStore()
  const inputRef = useRef(input)
  const outputRef = useRef(output)
  inputRef.current = input
  outputRef.current = output

  const [registrationStatus, setRegistrationStatus] =
    useState<RegistrationStatusWithDisabled>('loading')
  const [userAgentConnectionStatus, setUserAgentConnectionStatus] =
    useState<UserAgentConnectionStatus>('disconnected')

  const activeCall = calls.find(call =>
    [
      SessionState.Initial,
      SessionState.Establishing,
      SessionState.Established,
    ].includes(call.sessionState)
  )
  const activeCallRef = useRef<CallBoth | undefined>(activeCall)
  activeCallRef.current = activeCall

  const hide = () => setShownCallId(undefined)
  const hideRef = useRef(hide)

  useEffect(() => {
    iframeContext.parentRef.current?.setSipRegistrationStatus(
      registrationStatus
    )
  }, [registrationStatus])

  useEffect(() => {
    if (activeCall) {
      iframeContext.parentRef.current?.startedCall()

      if (activeCall.establishedAt) {
        iframeContext.parentRef.current?.establishedAt(activeCall.establishedAt)
      }
    } else {
      iframeContext.parentRef.current?.endedCall()
    }
  }, [activeCall])

  useEffect(() => {
    iframeContext.parentRef.current?.setSipRegistrationStatus(
      registrationStatus
    )
  }, [iframeContext.isLoaded])

  useEffect(() => {
    const newShown = calls.find(
      call =>
        [SessionState.Establishing, SessionState.Established].includes(
          call.sessionState
        ) ||
        (!call.isIncoming && call.sessionState == SessionState.Initial)
    )

    if (newShown && (!shownCallId || shownCallId != newShown.sessionId)) {
      setShownCallId(newShown.sessionId)
    }
  }, [calls.map(call => call.sessionState).join()])

  useEffect(() => {
    if (wsStatus == 'success') {
      connect(activeTab)
    }
  }, [wsStatus])

  useEffect(() => {
    if (!userChannel) {
      return
    }

    userChannel.on('activate_tab', ({ uuid }) => {
      if (tabUuid == uuid) {
        setTimeout(() => {
          registerRef.current && registerRef.current()
        }, 1000)
      }
    })

    userChannel.on('deactivate_tab', ({ uuid }) => {
      if (tabUuid == uuid) {
        unregisterRef.current && unregisterRef.current()
      }
    })

    userChannel.on('transferAccepted', () => {
      setCalls(calls =>
        calls.map(call =>
          call.sessionState == SessionState.Established
            ? { ...call, transferedToMeStatus: 'accepted' }
            : call
        )
      )
    })
  }, [!!userChannel])

  const connect = async (withRegistration: boolean) => {
    const transportOptions: TransportOptions = {
      server: `wss://${domain}`,
      keepAliveInterval: 60, // domyślnie wyłączony, nie ma dowodów by pomagało
    }

    const userAgentOptions: UserAgentOptions = {
      uri: UserAgent.makeURI(`sip:${login}@${domain}`),
      authorizationUsername: login,
      authorizationPassword: password,
      transportOptions,
      logLevel: 'warn',
      sessionDescriptionHandlerFactoryOptions: {
        iceGatheringTimeout: 1000,
        peerConnectionConfiguration: {
          iceServers: [
            ...iceServers,
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' },
            { urls: 'stun:stun3.l.google.com:19302' },
            { urls: 'stun:stun4.l.google.com:19302' },
          ],
        },
      } as SessionDescriptionHandlerConfiguration,
      sessionDescriptionHandlerFactory:
        Web.defaultSessionDescriptionHandlerFactory(async () =>
          navigator.mediaDevices.getUserMedia({
            audio: inputRef.current
              ? {
                  deviceId: inputRef.current,
                }
              : true,
          })
        ),
    }

    if (iceServers.length) {
      ;(
        userAgentOptions!
          .sessionDescriptionHandlerFactoryOptions! as SessionDescriptionHandlerConfiguration
      ).peerConnectionConfiguration!.iceTransportPolicy = 'relay'
    }

    const userAgent = new UserAgent(userAgentOptions)

    //

    userAgent.delegate = {
      onRegister() {
        // console.log('register')
      },

      onRegisterRequest() {
        // console.log('register request')
      },

      onConnect() {
        // console.log('connected')
        setUserAgentConnectionStatus('connected')
      },

      onDisconnect() {
        // console.log('disconnected')
        setUserAgentConnectionStatus('disconnected')
      },

      onInvite(invitation: Invitation): void {
        notificationsContext.notify(
          intl.formatMessage({ id: 'notification / new call' })
        )

        iframeContext.parentRef.current?.open()

        // An Invitation is a Session
        const incomingSession: Session = invitation

        incomingSession.delegate = {}

        incomingSession.stateChange.addListener((state: SessionState) => {
          if (state == SessionState.Terminated) {
            inboundSoundPause()
          }

          setCalls(calls =>
            sessionStateHandler(
              calls,
              incomingSession,
              state,
              audioRef.current as HTMLAudioElement,
              outputRef.current
            )
          )
        })

        const answerOptions: InvitationAcceptOptions = {
          sessionDescriptionHandlerOptions: {
            constraints: {
              audio: true,
              video: false,
            },
          },
        }

        const number = invitation.remoteIdentity.uri.user as string

        const uuid =
          invitation['incomingInviteRequest'].message.headers[
            'X-Commai-Calluuid'
          ][0].raw

        setCalls(calls => [
          ...calls,
          {
            uuid,
            sessionId: invitation.id,
            isIncoming: true,
            number,
            sessionState: SessionState.Initial,
            answer: () => {
              hideRef.current()
              history.push('/phone')
              inboundSoundPause()
              invitation.accept(answerOptions)
            },
            reject: () => {
              inboundSoundPause()
              invitation.reject()
            },
            bye: () => invitation.bye(),
            muted: false,
            sendDtmf: sendDtmf(invitation),
            transferStatus: false,
            setTransferStatus: (status: TransferStatus) => {
              setCalls(calls =>
                calls.map(call =>
                  call.uuid == uuid ? { ...call, transferStatus: status } : call
                )
              )
            },
            transferedToMeStatus: false,
          },
        ])

        get<GetCallDataResp>('calls/get/inbound/' + uuid).then(data => {
          setCalls(calls =>
            calls.map(call =>
              call.uuid == uuid
                ? (() => {
                    const transferedToMe =
                      data.call.userId && data.call.userId != window.c.userId

                    if (transferedToMe) {
                      setStorage(data.call.id, data.call)
                    }

                    return {
                      ...call,
                      id: data.call.id,
                      apiData: convertCall(data.call),
                      client: data.client,
                      transferedToMeStatus: transferedToMe ? 'started' : false,
                    } as IncomingCall
                  })()
                : call
            )
          )
        })

        inboundSound.play()
      },
    }

    //

    const registererOptions: RegistererOptions = {}
    const registerer = new Registerer(userAgent, registererOptions)

    const register = async () => {
      const registerRequest = await registerer.register()

      const originalOnAccept = registerRequest.delegate?.onAccept
      const originalOnReject = registerRequest.delegate?.onReject

      registerRequest.delegate = {
        ...registerRequest.delegate,
        onAccept(e) {
          originalOnAccept && originalOnAccept(e)
          setRegistrationStatus('success')
        },
        onReject(e) {
          originalOnReject && originalOnReject(e)
          setRegistrationStatus('fail')
        },
      }
    }

    registerRef.current = () => {
      register()
    }

    unregisterRef.current = () => {
      registerer.unregister()
      setRegistrationStatus('disabled')
    }

    userAgent.start().then(async () => {
      makeCallRef.current = (number, payload = {}) => {
        number = number + ''

        if (number.startsWith('00')) {
          number = number.substring(2)
        }

        if (number.length < 5) {
          return
        }

        if (number.length == 9) {
          number = '48' + number
        }

        if (activeCallRef.current) {
          return
        }

        if (window['describeFirst']) {
          window['describeFirst']()
          return
        }

        hide()

        const target = UserAgent.makeURI(
          `sip:${number.replace(/[^0-9]/g, '')}@${domain}`
        )
        if (!target) {
          throw new Error('Failed to create target URI.')
        }

        history.push('/phone')

        const callbackUuid = uuidv4()

        const inviter = new Inviter(userAgent, target, {
          extraHeaders: [
            'X-Callback-Payload: ' +
              JSON.stringify({ ...payload, callbackUuid }),
          ],
        })

        const outgoingSession: Session = inviter

        setCalls(calls => [
          ...calls,
          {
            uuid: callbackUuid,
            sessionId: outgoingSession.id,
            isIncoming: false,
            number,
            sessionState: SessionState.Initial,
            bye: () => outgoingSession.bye(),
            cancel: () => inviter.cancel(),
            muted: false,
            sendDtmf: sendDtmf(outgoingSession),
          },
        ])

        outgoingSession.stateChange.addListener((state: SessionState) => {
          if (state == SessionState.Establishing) {
            // howler
            outboundSound.play()

            get<GetCallDataResp>('calls/get/outbound/' + callbackUuid).then(
              data => {
                setCalls(calls =>
                  calls.map(call =>
                    call.uuid == callbackUuid
                      ? {
                          ...call,
                          id: data.call.id,
                          apiData: convertCall(data.call),
                          client: data.client,
                        }
                      : call
                  )
                )
              }
            )
          }

          if (
            [SessionState.Established, SessionState.Terminated].includes(state)
          ) {
            // howler
            outboundSoundPause()
          }

          setCalls(calls =>
            sessionStateHandler(
              calls,
              outgoingSession,
              state,
              audioRef.current as HTMLAudioElement,
              outputRef.current
            )
          )
        })

        // Send the INVITE request
        inviter.invite()
        // .then(invitation => {})
        // .catch((error: Error) => {})
      }

      if (withRegistration) {
        register()
      } else {
        setRegistrationStatus('disabled')
      }
    })
  }

  const toggleMute = (uuid: string) => {
    setCalls(calls =>
      calls.map(call => {
        if (call.uuid !== uuid || !call.peerConnection) {
          return call
        }

        const newMuted = !call.muted

        call.peerConnection.getSenders().forEach(sender => {
          if (sender.track) {
            sender.track.enabled = !newMuted
          }
        })

        return {
          ...call,
          muted: newMuted,
        }
      })
    )
  }

  iframeContext.makeCallRef.current = makeCallRef.current

  return (
    <SipContext.Provider
      value={{
        registrationStatus,
        userAgentConnectionStatus,
        calls,
        makeCall: makeCallRef.current,
        incomingCall: calls.find(
          call => call.isIncoming && call.sessionState == SessionState.Initial
        ) as IncomingCall,
        activeCall,
        activeEstablishedCall: calls.find(
          call => call.sessionState == SessionState.Established
        ),
        hide,
        shownCall: calls.find(call => call.sessionId == shownCallId),
        toggleMute,
      }}
    >
      {children}
      <audio ref={audioRef} />
    </SipContext.Provider>
  )
}

export default SipConn
